import type { QueryClient } from '@tanstack/react-query';
import { notFound, redirect } from '@tanstack/react-router';
import { z } from 'zod';

import { hasPermission } from '@/entities/practice-member';
import { checkExpiresWithin } from '@/entities/user';
import { signInUrlAfterEndingSession } from '@/shared/api/redirect-to-sign-in';

import { SESSION_EXPIRY_HEADROOM_MINUTES } from '../config/recording.config';

export const recordingSearchSchema = z.object({
    clientId: z.string().optional(),
});

export type RecordingSearch = z.infer<typeof recordingSearchSchema>;

type RecordingBeforeLoadArgs = {
    context: { queryClient: QueryClient };
    search: RecordingSearch;
};

export async function recordingBeforeLoad({ context, search }: RecordingBeforeLoadArgs) {
    if (!(await hasPermission(context.queryClient, ['CLINICIAN', 'NOTES_ACTIVE']))) {
        throw redirect({ to: '/dashboard' });
    }

    if (!search.clientId) {
        throw notFound();
    }

    // Refresh here rather than on the recording screen: that screen now starts recording on mount,
    // so returning from sign-in to /recording would open a live mic straight after a password entry.
    if (await checkExpiresWithin(SESSION_EXPIRY_HEADROOM_MINUTES)) {
        throw redirect({
            href: signInUrlAfterEndingSession(true, `${window.location.origin}/dashboard`),
        });
    }
}
